import get from 'lodash/get';
import trim from 'lodash/trim';
import { RelationshipsPeopleItemModel, AddressNameField } from 'forms/sections/relationships/people/types';
import { LocationModel, NameModel } from 'forms/shared/types';
import { countryString } from '../validators/location';

const getNameAsString = (name: NameModel | string): string => {
  const fullName: string = (typeof (name) === 'string') ? name : `${name?.first || ''} ${name?.middle || ''} ${name?.last || ''}`;
  return trim(fullName?.replace(/\s+/g, ' '));
};

const getAddressAsString = (address: LocationModel): string => {
  const fullAddress = countryString(address?.country) === 'United States'
    ? `${address?.street || ''} ${address?.street2 || ''} ${address?.city || ''} ${address?.state || ''} ${address.zipcode || ''}`
    : `${address?.street || ''} ${address?.street2 || ''} ${address?.city || ''} ${countryString(address?.country) || ''}`;
  return trim(fullAddress?.replace(/\s+/g, ' '));
};

export const getDuplicateNamesAndAddresses = (
  listItems: any = [],
  peopleWhoKnowYouWell: RelationshipsPeopleItemModel[] = [],
  addressNameFields: AddressNameField[] = [],
  applicantAddress?: LocationModel
) => {
  const results: any = { warnings: [], errors: [] };

  listItems?.forEach?.((item: any, index: number) => {
    addressNameFields?.forEach?.(({ addressFields, nameField }) => {
      const name: NameModel | string = get(item, nameField);
      const fullName = getNameAsString(name);
      const addresses: LocationModel[] = addressFields?.map((addressField): LocationModel => get(item, addressField)) || [];

      if (applicantAddress) {
        addresses.push(applicantAddress);
      }

      peopleWhoKnowYouWell?.forEach?.((personItem) => {
        const personName: NameModel | string = get(personItem, 'Item.Name');
        const personAddress: LocationModel = get(personItem, 'Item.Address');
        const personFullName = getNameAsString(personName);
        const personFullAddress = getAddressAsString(personAddress);

        // we do not want to run the check if personFullName is empty; at the very least, a last name is required
        if (personName && personAddress && personFullName && personFullAddress) {
          if (fullName?.toLowerCase()
            === personFullName?.toLowerCase()) {
            let addressMatches = false;
            addresses?.forEach?.((address) => {
              const fullAddress = getAddressAsString(address);
              if (fullAddress?.toLowerCase()
                === personFullAddress?.toLocaleLowerCase()) {
                addressMatches = true;
                const message = `Item ${index + 1}: Name: ${fullName}; Address: ${fullAddress}`;
                if (!results?.errors?.includes(message)) {
                  results?.errors?.push(message);
                }
              }
            });
            if (!addressMatches) {
              const message = `Item ${index + 1}: Name: ${fullName}`;
              if (!results?.warnings?.includes(message)) {
                results?.warnings?.push(message);
              }
            }
          }
        }
      });
    });
  });
  return results;
};

/*
  Returns a list of duplicate people given only one list
  Name and Address must be immediate properties of Item
*/
export const getDuplicatePeople = (peopleList: any) => {
  const results: any = { warnings: [], errors: [] };

  if (peopleList) {
    for (let i = 0; i < peopleList?.length; i++) {
      const currentName = getNameAsString(get(peopleList[i], 'Item.Name'));
      const currentAddress = getAddressAsString(get(peopleList[i], 'Item.Address'));
      let duplicateError = false;
      let duplicateWarning = false;

      if (currentName && currentAddress) {
        for (let j = 0; j < peopleList.length; j++) {
          if (i !== j) {
            const comparisonName = getNameAsString(get(peopleList[j], 'Item.Name'));
            const comparisonAddress = getAddressAsString(get(peopleList[j], 'Item.Address'));

            if (currentName?.toLowerCase() === comparisonName?.toLowerCase()) {
              if (currentAddress?.toLowerCase() === comparisonAddress?.toLowerCase()) {
                duplicateError = true;
                duplicateWarning = true;
              } else {
                duplicateWarning = true;
              }
              break;
            }
          }
        }
      }

      if (duplicateError) {
        results.errors.push(`Item ${i + 1}: Name: ${currentName}; Address: ${currentAddress}`);
      }
      if (duplicateWarning) {
        results.warnings.push(`Item ${i + 1}: Name: ${currentName}`);
      }
    }
  }

  return results;
};
